import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Disc, Music } from 'lucide-react';
import { AudioFeatures } from '@meradio/shared-types';
import { parseTrackTitle } from '@meradio/music-utils';
import { useAppDispatch } from '@/store/store';
import { setCurrentVideo, addToQueue } from '@/features/player/playerSlice';
import { analyzeVideo } from '@/services/audio.api';
import { getVideosByAlbum, ArtistVideo } from '@/services/artists.api';
import VideoTable from '@/components/table/VideoTable';
import AddToCratePopover from '@/features/crates/AddToCratePopover';

interface AlbumVideo {
  id: string;
  videoId: string;
  channelId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  publishedAt: Date;
  dateAdded: Date;
  createdAt: Date;
  updatedAt: Date;
  isLiked?: boolean;
  channel?: {
    id?: string;
    channelId?: string;
    name?: string;
    thumbnailUrl?: string;
  };
}

const toAlbumVideo = (item: ArtistVideo): AlbumVideo => ({
  id: item.id,
  videoId: item.videoId,
  channelId: item.channel?.channelId || item.channel?.id || item.channelId || '',
  title: item.title,
  description: item.description,
  thumbnailUrl: item.thumbnailUrl,
  publishedAt: new Date(item.publishedAt),
  dateAdded: new Date(item.publishedAt),
  createdAt: new Date(item.publishedAt),
  updatedAt: new Date(item.publishedAt),
  isLiked: item.isLiked,
  channel: item.channel,
});

const AlbumPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const dispatch = useAppDispatch();

  const [items, setItems] = useState<ArtistVideo[]>([]);
  const [audioFeatures, setAudioFeatures] = useState<Record<string, AudioFeatures>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crateTarget, setCrateTarget] = useState<{ videoId: string; title: string } | null>(null);

  const albumName = name ? decodeURIComponent(name) : '';

  const videos = useMemo(() => items.map(toAlbumVideo), [items]);

  const artists = useMemo(() => {
    const names = new Set<string>();
    videos.forEach(video => {
      const artist = parseTrackTitle(video.title).artist;
      if (artist) names.add(artist);
    });
    return Array.from(names);
  }, [videos]);

  useEffect(() => {
    if (!albumName) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getVideosByAlbum(albumName)
      .then(result => {
        if (cancelled) return;
        setItems(result);
        const features: Record<string, AudioFeatures> = {};
        result.forEach(item => {
          if (item.audioFeatures) features[item.videoId] = item.audioFeatures;
        });
        setAudioFeatures(features);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load album');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [albumName]);

  const handlePlayVideo = (video: AlbumVideo) => {
    dispatch(setCurrentVideo(video as any));
  };

  const handleAddToQueue = (video: AlbumVideo) => {
    dispatch(addToQueue(video as any));
  };

  const handleAnalyze = async (video: AlbumVideo) => {
    try {
      await analyzeVideo(video.videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-neutral-300 dark:bg-bg-dark-tertiary" />
          <div className="h-64 rounded-xl bg-neutral-200 dark:bg-bg-dark-secondary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-6">
          <p className="text-danger-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 pt-4 md:p-6 max-w-7xl mx-auto">
      <div className="px-4 md:px-0 mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Disc className="w-8 h-8 text-meradio-primary flex-shrink-0" />
          <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary truncate">
            {albumName}
          </h1>
        </div>
        {artists.length > 0 && (
          <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary">
            {artists.map((artist, index) => (
              <React.Fragment key={artist}>
                {index > 0 && ', '}
                <Link
                  to={`/artists/${encodeURIComponent(artist)}`}
                  className="hover:text-meradio-primary hover:underline"
                >
                  {artist}
                </Link>
              </React.Fragment>
            ))}
          </p>
        )}
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-12">
          <Music className="w-12 h-12 text-neutral-400 dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-text-dark-primary mb-2">
            No tracks found
          </h3>
        </div>
      ) : (
        <VideoTable
          videos={videos}
          audioFeatures={audioFeatures}
          onPlayVideo={handlePlayVideo}
          onAddToQueue={handleAddToQueue}
          onAnalyze={handleAnalyze}
          onAddToCrate={video => setCrateTarget({ videoId: video.videoId, title: video.title })}
        />
      )}

      <AddToCratePopover
        videoId={crateTarget?.videoId ?? null}
        videoTitle={crateTarget?.title}
        open={crateTarget !== null}
        onClose={() => setCrateTarget(null)}
      />
    </div>
  );
};

export default AlbumPage;
